import { useEffect, useMemo, useState } from 'react';
import {
  apiClient,
  ApiError,
  type EventSummary,
  type AllProjectsRow,
  type TaskListRow,
  type TaskPriority,
} from '@/lib/api/client';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { cn } from '@/lib/utils';

interface Props {
  event: EventSummary;
  onConverted: (args: { task_id: string; project_id: string }) => void;
  onClose: () => void;
}

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  p0: 'P0 · Urgent',
  p1: 'P1 · High',
  p2: 'P2 · Normal',
  p3: 'P3 · Low',
};

const SEVERITY_TO_PRIORITY: Record<string, TaskPriority> = {
  urgent: 'p0',
  warn: 'p1',
  info: 'p2',
};

/**
 * Convert an inbox event into a task (or subtask) under any project the
 * user belongs to. Project picker is required; optional parent-task
 * picker (loaded after a project is chosen) makes it a subtask. Priority
 * defaults to the event severity → P0/P1/P2 mapping.
 *
 * Server is the source of truth for "already converted" — a 409 surfaces
 * the existing task_id so the user can jump to it instead of creating a
 * duplicate.
 */
export function ConvertToTaskModal({ event, onConverted, onClose }: Props) {
  const formRef = useDialogFocus<HTMLFormElement>();
  const [projects, setProjects] = useState<AllProjectsRow[] | null>(null);
  const [projectId, setProjectId] = useState<string>(event.project_id ?? '');
  const [parentTasks, setParentTasks] = useState<TaskListRow[] | null>(null);
  const [parentTaskId, setParentTaskId] = useState<string>('');
  const [priority, setPriority] = useState<TaskPriority>(
    SEVERITY_TO_PRIORITY[event.severity] ?? 'p2',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ task_id: string } | null>(null);

  // Load projects on mount.
  useEffect(() => {
    apiClient
      .allProjects()
      .then((r) => setProjects(r.projects))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Failed to load projects'));
  }, []);

  // When the chosen project changes, load top-level tasks for the parent
  // picker. Silent — empty list just means "no parents available."
  useEffect(() => {
    if (!projectId) {
      setParentTasks(null);
      setParentTaskId('');
      return;
    }
    setParentTaskId('');
    apiClient
      .listTasks(projectId, { parent_task_id: 'top' })
      .then((r) => setParentTasks(r.tasks.filter((t) => t.status !== 'archived')))
      .catch(() => setParentTasks([]));
  }, [projectId]);

  const sortedProjects = useMemo(
    () =>
      projects
        ? [...projects].sort((a, b) =>
            (a.home_ws_name + ' / ' + a.name).localeCompare(b.home_ws_name + ' / ' + b.name),
          )
        : null,
    [projects],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) {
      setErr('Pick a project');
      return;
    }
    setBusy(true);
    setErr(null);
    setConflict(null);
    try {
      const r = await apiClient.convertEventToTask(event.event_id, {
        project_id: projectId,
        parent_task_id: parentTaskId || null,
        priority,
      });
      onConverted({ task_id: r.task_id, project_id: r.project_id });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // The server includes `task_id` on the 409 (inside the error obj)
        // so the user can open the already-existing task directly.
        const errObj = (e.detail as { error?: { task_id?: string } } | undefined)?.error;
        if (errObj?.task_id) setConflict({ task_id: errObj.task_id });
        setErr('This event was already converted. Open the existing task instead.');
      } else {
        setErr(e instanceof ApiError ? e.message : 'Conversion failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="convert-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        ref={formRef}
        onSubmit={submit}
        className="section-glow w-full max-w-md space-y-5 rounded-2xl bg-card p-7 shadow-2xl"
      >
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Convert event
          </div>
          <h2 id="convert-title" className="text-xl font-semibold tracking-tight">
            Make a task from "{event.title.length > 64 ? event.title.slice(0, 64) + '…' : event.title}"
          </h2>
          <p className="text-sm text-muted-foreground">
            From <span className="font-mono">{event.source_name}</span> · severity{' '}
            <span className="font-mono">{event.severity}</span>
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="convert-project"
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Project
          </label>
          <select
            id="convert-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            required
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="" disabled>
              {projects === null ? 'Loading projects…' : 'Pick a project'}
            </option>
            {sortedProjects?.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.home_ws_name} / {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="convert-parent"
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Parent task · optional (makes it a subtask)
          </label>
          <select
            id="convert-parent"
            value={parentTaskId}
            onChange={(e) => setParentTaskId(e.target.value)}
            disabled={!projectId || parentTasks === null}
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">— top-level task —</option>
            {parentTasks?.map((t) => (
              <option key={t.task_id} value={t.task_id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="convert-priority"
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Priority
          </label>
          <div className="flex gap-2" role="radiogroup" aria-label="Priority">
            {(['p0', 'p1', 'p2', 'p3'] as TaskPriority[]).map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={priority === p}
                onClick={() => setPriority(p)}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors',
                  priority === p
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
                )}
              >
                {PRIORITY_LABEL[p]}
              </button>
            ))}
          </div>
        </div>

        {err && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
          >
            {err}
            {conflict && (
              <button
                type="button"
                onClick={() => onConverted({ task_id: conflict.task_id, project_id: projectId })}
                className="ml-2 font-mono text-[10px] uppercase tracking-widest text-primary underline-offset-2 hover:underline"
              >
                Open existing task →
              </button>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!projectId || busy}
            className="flex-1 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Converting…' : parentTaskId ? 'Add as subtask' : 'Create task'}
          </button>
        </div>
      </form>
    </div>
  );
}
